import { useMemo, useState } from "react";
import { browseItems, browseTags } from "@/mocks/films";

export default function BrowseFilter() {
  const [activeTag, setActiveTag] = useState("全部");
  const [keyword, setKeyword] = useState("");

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return browseItems.filter((item) => {
      const matchTag = activeTag === "全部" || item.tags.includes(activeTag);
      const matchKw =
        kw.length === 0 ||
        item.title.toLowerCase().includes(kw) ||
        item.meta.toLowerCase().includes(kw) ||
        item.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchTag && matchKw;
    });
  }, [activeTag, keyword]);

  const resetAll = () => {
    setActiveTag("全部");
    setKeyword("");
  };

  return (
    <section id="browse" className="w-full bg-background-50 scroll-mt-20">
      <div className="w-full px-4 md:px-8 py-16 md:py-24">
        <div className="max-w-5xl mx-auto">
          <header className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs md:text-sm font-label text-secondary-800 bg-secondary-100 px-3 py-1.5 rounded-full">
              <i className="ri-list-check-2" aria-hidden="true"></i>
              片单筛选
            </span>
            <h2 className="mt-4 font-heading text-2xl md:text-4xl font-black text-foreground-950 leading-snug">
              按标签或关键词，自己拼一份片单
            </h2>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
              下面的筛选只作用于本页已有的条目，不会联网、不会保存你的输入。先点标签缩小范围，再用关键词进一步查找。
            </p>
          </header>

          <div className="mt-8 md:mt-10 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:justify-between">
              <div className="flex flex-wrap gap-2">
                {browseTags.map((tag) => {
                  const active = tag === activeTag;
                  return (
                    <button
                      key={tag}
                      type="button"
                      aria-pressed={active}
                      onClick={() => setActiveTag(tag)}
                      className={`px-3.5 py-1.5 rounded-full text-xs md:text-sm font-label whitespace-nowrap transition-colors cursor-pointer ${
                        active
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-800"
                      }`}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>

              <div className="relative w-full md:w-64 shrink-0">
                <i
                  className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"
                  aria-hidden="true"
                ></i>
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索片名 / 类型 / 看点"
                  aria-label="搜索片单"
                  className="w-full pl-9 pr-9 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                />
                {keyword.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setKeyword("")}
                    aria-label="清除搜索"
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-200 cursor-pointer"
                  >
                    <i className="ri-close-line" aria-hidden="true"></i>
                  </button>
                )}
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between gap-3 border-t border-background-200 pt-3">
              <p className="text-xs md:text-sm text-foreground-500 font-label">
                共 <span className="text-primary-600 font-semibold">{filtered.length}</span> 条结果
                {activeTag !== "全部" && <span> · 当前标签「{activeTag}」</span>}
              </p>
              <button
                type="button"
                onClick={resetAll}
                className="text-xs md:text-sm text-foreground-600 hover:text-primary-600 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line mr-1" aria-hidden="true"></i>
                重置筛选
              </button>
            </div>
          </div>

          {filtered.length > 0 ? (
            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {filtered.map((item) => (
                <li
                  key={item.id}
                  className="rounded-lg border border-background-200 bg-background-50 p-4 hover:border-primary-300 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950">{item.title}</h3>
                    <span className="shrink-0 text-xs text-accent-700 font-label bg-accent-100 px-2 py-0.5 rounded-md">
                      {item.year}
                    </span>
                  </div>
                  <p className="mt-1.5 text-xs md:text-sm text-foreground-600">{item.meta}</p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {item.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 rounded-full text-[11px] bg-secondary-100 text-secondary-900 font-label"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="mt-5 rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-12 text-center">
              <span className="mx-auto w-12 h-12 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-inbox-archive-line text-2xl" aria-hidden="true"></i>
              </span>
              <p className="mt-3 text-sm text-foreground-700">没有匹配的条目</p>
              <p className="mt-1 text-xs text-foreground-500">试试更短的词，或点「重置筛选」回到全部片单。</p>
              <button
                type="button"
                onClick={resetAll}
                className="mt-4 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer"
              >
                重置筛选
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}